import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import customFetcher from '../../utils/fetchInstance';
import { ContextData, ContextState } from './types';

type Props = {
	children: ReactNode;
};

const ContractsContext = React.createContext<ContextData>(null as any);

const initialState: ContextState = {
	page: 0,
	isInit: false,
	isLoading: false,
	filterStatus: '',
	contracts: null,
	filterText: '',
};

const ContractsProvider: React.FC<Props> = ({ children }) => {
	const [state, setState] = useState(initialState);

	const getContracts = useCallback(async (filterStatus: string, filterText: string) => {
		try {
			const { data } = await customFetcher(
				`/contracts?status=${encodeURI(filterStatus)}&search=${encodeURI(filterText)}`
			);
			const { contracts } = data;
			setState(prevState => {
				return { ...prevState, contracts };
			});
		} catch (error) {
			console.log(error);
		}
	}, []);

	const setInitContracts = useCallback(async () => {
		setState(prevState => {
			return {
				...prevState,
				isInit: true,
				isLoading: true,
				page: 0,
				filterStatus: '',
				filterText: '',
			};
		});
		await getContracts('', '');

		setState(prevState => {
			return { ...prevState, isLoading: false };
		});
	}, [getContracts]);

	const removeIsInit = useCallback(async () => {
		setState({ ...state, isInit: false });
	}, [state]);

	const setPage = useCallback(
		(page: number): void => {
			setState({ ...state, page });
		},
		[state]
	);

	const setFilterStatus = useCallback(
		async (filterStatus: string) => {
			setState(prevState => {
				return { ...prevState, isLoading: true, page: 0, filterStatus };
			});

			await getContracts(filterStatus, state.filterText);

			setState(prevState => {
				return { ...prevState, isLoading: false };
			});
		},
		[getContracts, state.filterText]
	);

	const setFilterText = useCallback(
		async (filterText: string) => {
			setState(prevState => {
				return { ...prevState, isLoading: true, page: 0, filterText };
			});

			await getContracts(state.filterStatus, filterText);

			setState(prevState => {
				return { ...prevState, isLoading: false };
			});
		},
		[getContracts, state.filterStatus]
	);

	const refetchContracts = useCallback(async () => {
		setState(prevState => {
			return { ...prevState, isLoading: true };
		});

		await getContracts(state.filterStatus, state.filterText);

		setState(prevState => {
			return { ...prevState, isLoading: false };
		});
	}, [getContracts, state.filterStatus, state.filterText]);

	const value = useMemo(
		(): ContextData => ({
			state,
			setInitContracts,
			removeIsInit,
			setFilterStatus,
			setPage,
			refetchContracts,
			setFilterText,
		}),
		[state, setInitContracts, removeIsInit, setFilterStatus, setPage, refetchContracts, setFilterText]
	);
	return <ContractsContext.Provider value={value}>{children}</ContractsContext.Provider>;
};

const useContractsContext = () => {
	return useContext(ContractsContext);
};

export { ContractsProvider, useContractsContext };
